// Helpers de conflito e sync — extraído de App.jsx — Fase B
import { DB } from "./deviceStorage";
import { classifyConflictSeverity } from "./conflictRules";
import { stableJson, stripDeletedItems } from "./syncUtils";
import { apiCall } from "../sync/collectionApi";
import { migrateCollectionRecordNumbers, supportsRecordNumberMigration } from "./recordNumberMigration";

const PENDING_CONFLICTS_KEY = "fz:pending_conflicts";
const CONFLICT_RESOLUTIONS_KEY = "fz:conflict_resolutions";
const SYNC_AUDIT_KEY = "fz:sync_audit";
const SYNC_HEALTH_KEY = "fz:sync_health";
const USER_DELETION_LOG_KEY = "fz:userDeletionLog";

function buildDeletionTombstones(entries) {
  const out = {
    accountIds: new Set(),
    linkedAccountIds: new Set(),
    employeeIds: new Set(),
    usernames: new Set(),
  };
  for (const item of Array.isArray(entries) ? entries : []) {
    const accountId = String(item?.accountId || "").trim();
    const linkedAccountId = String(item?.linkedAccountId || "").trim();
    const employeeId = String(item?.employeeId || "").trim();
    const username = String(item?.username || "").trim().toLowerCase();
    if (accountId) out.accountIds.add(accountId);
    if (linkedAccountId) out.linkedAccountIds.add(linkedAccountId);
    if (employeeId) out.employeeIds.add(employeeId);
    if (username) out.usernames.add(username);
  }
  return out;
}

function pruneDeletedAccounts(accounts, deletionLog) {
  const tomb = buildDeletionTombstones(deletionLog);
  return (Array.isArray(accounts) ? accounts : []).filter(acc => {
    const id = String(acc?.id || "").trim();
    const username = String(acc?.username || "").trim().toLowerCase();
    const isDefaultAdmin = username === "admin" && String(acc?.role || "") === "admin";
    if (isDefaultAdmin) return true;
    if (id && (tomb.accountIds.has(id) || tomb.linkedAccountIds.has(id))) return false;
    if (username && tomb.usernames.has(username)) return false;
    return true;
  });
}

function pruneDeletedEmployees(list, deletionLog) {
  const tomb = buildDeletionTombstones(deletionLog);
  return (Array.isArray(list) ? list : []).filter(emp => {
    const id = String(emp?.id || "").trim();
    const linkedAccountId = String(emp?.linkedAccountId || "").trim();
    const username = String(emp?.username || "").trim().toLowerCase();
    if (id && tomb.employeeIds.has(id)) return false;
    if (linkedAccountId && (tomb.accountIds.has(linkedAccountId) || tomb.linkedAccountIds.has(linkedAccountId))) return false;
    if (username && tomb.usernames.has(username)) return false;
    return !(emp?._deleted || emp?.deletedAt || emp?.deleted_at);
  });
}

// eslint-disable-next-line no-unused-vars
async function sanitizeCollectionForView(collectionName, data) {
  const migration = supportsRecordNumberMigration(collectionName) ? migrateCollectionRecordNumbers(collectionName, data) : null;
  const list = migration?.changed ? migration.records : (Array.isArray(data) ? data : (data ?? []));
  if (collectionName === "employees") {
    try {
      const deletionLog = await DB.get(USER_DELETION_LOG_KEY);
      return pruneDeletedEmployees(list, deletionLog);
    } catch (_) {
      return list.filter(emp => !(emp?._deleted || emp?.deletedAt || emp?.deleted_at));
    }
  }
  if (collectionName === "vet") {
    return stripDeletedItems(list);
  }
  return list;
}

function sanitizeGlobalCollectionForView(collectionName, data) {
  const list = Array.isArray(data) ? data : (data ?? []);
  if (collectionName === "suppliers" || collectionName === "providers" || collectionName === "people") {
    return stripDeletedItems(list);
  }
  return list;
}

const COLLECTION_LABELS = {
  orders:"Compras",
  animals:"Animais",
  financial:"Financeiro",
  employees:"Colaboradores",
  checklists:"Checklists",
  vet:"Veterinário",
  stock:"Estoque",
  people:"Pessoas",
  machinery:"Maquinário",
  pastures:"Pastagens",
  poi:"Pontos de Interesse",
  services:"Serviços Internos",
  outsourcedservices:"Serviços Terceirizados",
  occurrences:"Ocorrências",
  inspections:"Vistorias",
  invoices:"Notas fiscais",
};

function collectionLabel(collection) {
  return COLLECTION_LABELS[collection] || collection;
}

function describeConflictRecord(item) {
  if (!item || typeof item !== "object") return "";
  return (
    item.title ||
    item.name ||
    item.nome ||
    item.description ||
    item.descricao ||
    item.label ||
    item.subject ||
    item.serviceName ||
    item.type ||
    item.category ||
    item.tag ||
    item.numero ||
    item.code ||
    item.id ||
    ""
  );
}

function normalizeConflictFieldName(field) {
  if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') return String(field).trim();
  if (!field || typeof field !== 'object') return '';
  const nested = field.field || field.name || field.label || field.key || field.path || field.id;
  if (typeof nested === 'string' || typeof nested === 'number' || typeof nested === 'boolean') return String(nested).trim();
  return '';
}


const CONFLICT_FIELD_LABELS = {
  id: 'ID',
  farmId: 'Fazenda',
  farmName: 'Fazenda',
  primaryFarmId: 'Fazenda principal',
  linkedAccountId: 'Conta vinculada',
  userId: 'Usuário vinculado',
  employeeId: 'Colaborador vinculado',
  name: 'Nome',
  nome: 'Nome',
  username: 'Usuário',
  email: 'E-mail',
  phone: 'Telefone',
  role: 'Perfil',
  status: 'Status',
  costCenter: 'Centro de custo',
  cpf: 'CPF',
  rg: 'RG',
  birthDate: 'Data de nascimento',
  pix: 'PIX',
  salary: 'Salário',
  hired: 'Admissão',
  notes: 'Observações',
  warnings: 'Alertas',
  collabLevel: 'Nível do colaborador',
  gestorVisibleFields: 'Campos visíveis para gestor',
  sharedFarms: 'Fazendas adicionais',
  title: 'Título',
  description: 'Descrição',
  category: 'Categoria',
  type: 'Tipo',
  dueDate: 'Data',
  startDate: 'Início',
  endDate: 'Fim',
};

const CONFLICT_INTERNAL_FIELDS = new Set([
  '_syncHistory','clientId','updatedAt','updatedBy','createdAt','deletedAt','deletedBy','version','history','_deleted'
]);

const CONFLICT_FIELD_ORDER = [
  'farmId','primaryFarmId','name','username','status','role','costCenter','email','phone','cpf','rg','birthDate','pix','salary','hired','sharedFarms','notes','warnings'
];

function getFriendlyConflictFieldLabel(field) {
  const key = normalizeConflictFieldName(field);
  if (!key) return 'Campo';
  if (CONFLICT_FIELD_LABELS[key]) return CONFLICT_FIELD_LABELS[key];
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function orderConflictKeys(keys) {
  const rows = Array.from(new Set((Array.isArray(keys) ? keys : []).map((key) => String(key || '').trim()).filter(Boolean)));
  return rows.sort((a, b) => {
    const ai = CONFLICT_FIELD_ORDER.indexOf(a);
    const bi = CONFLICT_FIELD_ORDER.indexOf(b);
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
    return getFriendlyConflictFieldLabel(a).localeCompare(getFriendlyConflictFieldLabel(b), 'pt-BR');
  });
}

function formatConflictBusinessValue(value, fieldName, farms = []) {
  const key = normalizeConflictFieldName(fieldName);
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    if (key === 'sharedFarms') {
      return value.map((entry) => formatConflictBusinessValue(entry, 'farmId', farms)).join(', ');
    }
    return value.map((entry) => formatConflictBusinessValue(entry, key, farms)).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number') {
    if (key === 'salary') {
      try { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value); } catch(e) {}
    }
    return String(value);
  }
  if (key === 'farmId' || key === 'primaryFarmId') {
    return (Array.isArray(farms) ? farms.find((farm) => String(farm?.id || '') === String(value || ''))?.name : '') || String(value);
  }
  if (key === 'role') {
    const map = { admin: 'Administrador', gestor: 'Gestor', colaborador: 'Colaborador', solicitante: 'Colaborador', user: 'Usuário' };
    return map[String(value).toLowerCase()] || String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    if (/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(trimmed)) {
      const dt = new Date(trimmed);
      if (!Number.isNaN(dt.getTime())) {
        return trimmed.includes('T') ? dt.toLocaleString('pt-BR') : dt.toLocaleDateString('pt-BR');
      }
    }
    return trimmed;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch(e) {
      return String(value);
    }
  }
  return String(value);
}

function getConflictSourceTimestamp(source) {
  if (!source || typeof source !== 'object') return 0;
  const raw = source.updatedAt || source.updated_at || source.lastModifiedAt || source.modifiedAt || source.createdAt || source.created_at;
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

function getConflictValueTone(baseRaw, localRaw, serverRaw, sourceName, stamps = {}) {
  const sameBaseLocal = stableJson(baseRaw) === stableJson(localRaw);
  const sameBaseServer = stableJson(baseRaw) === stableJson(serverRaw);
  const sameLocalServer = stableJson(localRaw) === stableJson(serverRaw);
  if (sameBaseLocal && sameBaseServer) return 'same';
  if (sameBaseLocal) return sourceName === 'server' ? 'different' : 'same';
  if (sameBaseServer) return sourceName === 'local' ? 'different' : 'same';
  if (sameLocalServer) return sourceName === 'base' ? 'different' : 'same';
  const ordered = [
    { name: 'base', ts: Number(stamps.base || 0) },
    { name: 'local', ts: Number(stamps.local || 0) },
    { name: 'server', ts: Number(stamps.server || 0) },
  ].sort((a, b) => a.ts - b.ts);
  const latest = ordered[2]?.name || 'server';
  return sourceName === latest ? 'latest' : 'older';
}

function buildConflictBusinessRows(item, farms = []) {
  const base = (item && typeof item.base === 'object' && item.base) ? item.base : {};
  const local = (item && typeof item.local === 'object' && item.local) ? item.local : {};
  const server = (item && typeof item.server === 'object' && item.server) ? item.server : {};
  const manualFieldNames = Array.isArray(item?.manualFields) ? item.manualFields.map((field) => normalizeConflictFieldName(field)).filter(Boolean) : [];
  const candidateKeys = orderConflictKeys([
    ...Object.keys(base || {}),
    ...Object.keys(local || {}),
    ...Object.keys(server || {}),
  ].filter((key) => !CONFLICT_INTERNAL_FIELDS.has(String(key || '').trim())));
  const stamps = {
    base: getConflictSourceTimestamp(base),
    local: getConflictSourceTimestamp(local),
    server: getConflictSourceTimestamp(server),
  };

  return candidateKeys.map((key) => {
    const baseRaw = base?.[key];
    const localRaw = local?.[key];
    const serverRaw = server?.[key];
    const baseValue = formatConflictBusinessValue(baseRaw, key, farms);
    const localValue = formatConflictBusinessValue(localRaw, key, farms);
    const serverValue = formatConflictBusinessValue(serverRaw, key, farms);
    const isConflict = manualFieldNames.includes(key) || stableJson(baseRaw) !== stableJson(localRaw) || stableJson(baseRaw) !== stableJson(serverRaw) || stableJson(localRaw) !== stableJson(serverRaw);
    if (!isConflict) return null;
    return {
      field: key,
      label: getFriendlyConflictFieldLabel(key),
      baseValue,
      localValue,
      serverValue,
      isConflict,
      tones: {
        base: getConflictValueTone(baseRaw, localRaw, serverRaw, 'base', stamps),
        local: getConflictValueTone(baseRaw, localRaw, serverRaw, 'local', stamps),
        server: getConflictValueTone(baseRaw, localRaw, serverRaw, 'server', stamps),
      },
    };
  }).filter(Boolean);
}

const INTERNAL_CONFLICT_FIELDS = new Set(['_syncHistory', 'clientId', 'updatedAt', 'updatedBy', 'createdAt', 'deletedAt', 'deletedBy', 'version', 'history']);

function isInternalConflictField(field) {
  return INTERNAL_CONFLICT_FIELDS.has(String(normalizeConflictFieldName(field) || '').trim());
}

function getMeaningfulConflictFields(fields) {
  const rows = Array.isArray(fields) ? fields : [];
  const meaningful = rows.filter((field) => !isInternalConflictField(field));
  return meaningful.length ? meaningful : rows;
}

function formatConflictFieldLabel(field) {
  return getFriendlyConflictFieldLabel(field);
}

function formatSyncHistoryForUI(value) {
  const rows = Array.isArray(value) ? value : [];
  if (!rows.length) return '—';
  const recent = rows.slice(-3).reverse().map((entry) => {
    const safe = (entry && typeof entry === 'object') ? entry : {};
    const at = safe.at ? (() => { try { return new Date(safe.at).toLocaleString('pt-BR'); } catch(e) { return safe.at; } })() : 'sem data';
    const by = safe.by || 'sistema';
    const typ = safe.type || safe.source || 'alteração';
    const changed = Array.isArray(safe.changedFields) && safe.changedFields.length ? ` · campos: ${safe.changedFields.join(', ')}` : '';
    return `• ${at} · ${by} · ${typ}${changed}`;
  });
  return `${rows.length} registro(s) no histórico\n${recent.join('\n')}`;
}

function formatConflictValueForUI(value, fieldName = '') {
  if (value == null) return '—';
  const normalizedField = normalizeConflictFieldName(fieldName);
  if (normalizedField === '_syncHistory') return formatSyncHistoryForUI(value);
  if (typeof value === 'string') return value.trim() || '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) {
    try { return value.toLocaleString('pt-BR'); } catch(e) {}
    return value.toISOString();
  }
  try {
    const json = JSON.stringify(value, null, 2);
    if (!json || json === 'null') return '—';
    return json.length > 4000 ? `${json.slice(0, 4000)}\n…` : json;
  } catch(e) {}
  return String(value);
}

function getConflictRecordById(items, id) {
  const rows = Array.isArray(items) ? items : [];
  return rows.find((row) => String(row?.id || '') === String(id || '')) || null;
}

function getConflictFieldValue(source, fieldName) {
  if (!source || typeof source !== 'object' || !fieldName) return undefined;
  const parts = String(fieldName).split('.').filter(Boolean);
  let current = source;
  for (const part of parts) {
    if (current == null) return undefined;
    if (Array.isArray(current)) {
      const index = Number(part);
      current = Number.isInteger(index) ? current[index] : undefined;
      continue;
    }
    if (typeof current !== 'object') return undefined;
    current = current[part];
  }
  return current;
}

function normalizeConflictFieldEntry(field, item) {
  const itemSafe = (item && typeof item === 'object') ? item : {};
  if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') {
    const fieldName = String(field).trim();
    return {
      field: fieldName,
      base: getConflictFieldValue(itemSafe.base, fieldName),
      local: getConflictFieldValue(itemSafe.local, fieldName),
      server: getConflictFieldValue(itemSafe.server, fieldName),
      chosen: itemSafe.suggestedResolution || '',
      manualRequired: true,
    };
  }
  if (!field || typeof field !== 'object') {
    return {
      field: '',
      base: undefined,
      local: undefined,
      server: undefined,
      chosen: itemSafe.suggestedResolution || '',
      manualRequired: true,
    };
  }
  const fieldName = normalizeConflictFieldName(field) || '';
  return {
    ...field,
    field: fieldName,
    base: field.base !== undefined ? field.base : getConflictFieldValue(itemSafe.base, fieldName),
    local: field.local !== undefined ? field.local : getConflictFieldValue(itemSafe.local, fieldName),
    server: field.server !== undefined ? field.server : getConflictFieldValue(itemSafe.server, fieldName),
    chosen: field.chosen || itemSafe.suggestedResolution || '',
    manualRequired: field.manualRequired !== undefined ? !!field.manualRequired : true,
  };
}

function buildConflictSummary(conflict) {
  const items = Array.isArray(conflict?.conflict_items) ? conflict.conflict_items : [];
  const local = Array.isArray(conflict?.local_data) ? conflict.local_data : [];
  const server = Array.isArray(conflict?.server_data) ? conflict.server_data : [];
  const previews = items.slice(0, 3).map((entry) => {
    const found = getConflictRecordById(local, entry?.id) || getConflictRecordById(server, entry?.id) || {};
    const label = describeConflictRecord(found);
    const rawFields = Array.isArray(entry?.manualFields) && entry.manualFields.length
      ? entry.manualFields
      : (Array.isArray(entry?.fields) ? entry.fields : []);
    const fields = getMeaningfulConflictFields(rawFields).map(normalizeConflictFieldName).filter(Boolean).slice(0, 4);
    return {
      id: entry?.id || found?.id || null,
      label,
      fields,
    };
  });
  const first = previews[0] || {};
  const firstLabel = first.label || (first.id ? `Registro ${first.id}` : `${items.length || 1} registro(s)`);
  const fieldList = (first.fields || []).join(', ');
  const changedFields = previews.flatMap(p => p.fields || []).filter((v, i, arr) => v && arr.indexOf(v) === i).slice(0, 8);
  return {
    title: `${collectionLabel(conflict?.collection)} · ${firstLabel}`,
    subject: firstLabel,
    entity_label: firstLabel,
    changed_fields: changedFields,
    record_count: items.length || 0,
    preview: previews,
    severity: classifyConflictSeverity(conflict?.collection, changedFields),
    confidence: typeof conflict?.confidence === 'number' ? conflict.confidence : 0,
    summary_text: fieldList ? `Campos com conflito: ${fieldList}` : `Conflito pendente em ${collectionLabel(conflict?.collection)}.`,
  };
}

async function loadPendingConflictsFromStorage() {
  try { return (await DB.get(PENDING_CONFLICTS_KEY)) || []; } catch(e) {}
  try { return JSON.parse(localStorage.getItem(PENDING_CONFLICTS_KEY) || "[]"); } catch(e) {}
  return [];
}

async function savePendingConflictsToStorage(list) {
  const next = Array.isArray(list) ? list : [];
  try { await DB.set(PENDING_CONFLICTS_KEY, next); } catch(e) {}
  try { localStorage.setItem(PENDING_CONFLICTS_KEY, JSON.stringify(next)); } catch(e) {}
  return next;
}


async function loadConflictResolutionsFromStorage() {
  try { return (await DB.get(CONFLICT_RESOLUTIONS_KEY)) || []; } catch(e) {}
  try { return JSON.parse(localStorage.getItem(CONFLICT_RESOLUTIONS_KEY) || "[]"); } catch(e) {}
  return [];
}

async function saveConflictResolutionsToStorage(list) {
  const next = Array.isArray(list) ? list : [];
  try { await DB.set(CONFLICT_RESOLUTIONS_KEY, next); } catch(e) {}
  try { localStorage.setItem(CONFLICT_RESOLUTIONS_KEY, JSON.stringify(next)); } catch(e) {}
  return next;
}

async function loadSyncAuditFromStorage() {
  try { return (await DB.get(SYNC_AUDIT_KEY)) || []; } catch(e) {}
  try { return JSON.parse(localStorage.getItem(SYNC_AUDIT_KEY) || "[]"); } catch(e) {}
  return [];
}

async function saveSyncAuditToStorage(list) {
  const next = Array.isArray(list) ? list : [];
  try { await DB.set(SYNC_AUDIT_KEY, next); } catch(e) {}
  try { localStorage.setItem(SYNC_AUDIT_KEY, JSON.stringify(next)); } catch(e) {}
  return next;
}

async function appendSyncAudit(entry) {
  const current = await loadSyncAuditFromStorage();
  const next = [{ id: `audit_${Date.now()}_${Math.random().toString(36).slice(2,8)}`, at: new Date().toISOString(), ...entry }, ...current].slice(0, 300);
  await saveSyncAuditToStorage(next);
  return next;
}

async function saveSyncHealthSnapshot(snapshot) {
  try { await DB.set(SYNC_HEALTH_KEY, snapshot); } catch(e) {}
  try { localStorage.setItem(SYNC_HEALTH_KEY, JSON.stringify(snapshot)); } catch(e) {}
  return snapshot;
}

// eslint-disable-next-line no-unused-vars
async function loadSyncHealthSnapshot() // eslint-disable-line no-unused-vars
/* istanbul ignore next */ {
  try { return (await DB.get(SYNC_HEALTH_KEY)) || null; } catch(e) {}
  try { return JSON.parse(localStorage.getItem(SYNC_HEALTH_KEY) || 'null'); } catch(e) {}
  return null;
}

function normalizeConflictForUI(conflict) {
  const row = (conflict && typeof conflict === 'object') ? conflict : {};
  const derived = buildConflictSummary(row);
  const asText = (value, fallback = '') => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (value == null) return fallback;
    if (typeof value === 'object') {
      const nested = value.field || value.name || value.label || value.id || value.value;
      if (typeof nested === 'string' || typeof nested === 'number' || typeof nested === 'boolean') return String(nested);
      return fallback;
    }
    return fallback;
  };
  const changedFields = getMeaningfulConflictFields(Array.isArray(row.changed_fields) && row.changed_fields.length ? row.changed_fields : derived.changed_fields)
    .map((field) => normalizeConflictFieldName(field) || asText(field, '').trim())
    .filter(Boolean)
    .filter((value, index, arr) => arr.indexOf(value) === index)
    .slice(0, 8);
  const previewSource = (Array.isArray(row.preview) && row.preview.length ? row.preview : derived.preview);
  const preview = previewSource
    .map((item, idx) => {
      const safe = (item && typeof item === 'object') ? item : {};
      const fields = getMeaningfulConflictFields(Array.isArray(safe.fields) ? safe.fields : [])
        .map((field) => normalizeConflictFieldName(field) || asText(field, '').trim())
        .filter(Boolean)
        .slice(0, 6);
      return {
        id: asText(safe.id, '') || `preview_${idx}`,
        label: asText(safe.label, '') || `Registro ${idx + 1}`,
        fields,
      };
    })
    .filter((item, idx, arr) => idx === arr.findIndex((entry) => String(entry.id || entry.label) === String(item.id || item.label)));
  const editingPresence = (Array.isArray(row.editing_presence) ? row.editing_presence : [])
    .map((entry) => (entry && typeof entry === 'object') ? entry : null)
    .filter(Boolean);
  const rawSummary = asText(row.summary_text, '');
  const summaryText = rawSummary && !rawSummary.includes('[object Object]') ? rawSummary : (derived.summary_text || '');
  const titleText = asText(row.title, '') || derived.title || '';
  const entityLabel = asText(row.entity_label, '') || derived.entity_label || '';
  return {
    ...row,
    conflict_id: row.conflict_id || row.id || `conflict_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    farm_id: asText(row.farm_id || row.farmId, ''),
    farm_name: asText(row.farm_name || row.farmName, ''),
    farmId: asText(row.farmId || row.farm_id, ''),
    farmName: asText(row.farmName || row.farm_name, ''),
    collection: asText(row.collection, ''),
    title: titleText,
    entity_label: entityLabel,
    summary_text: summaryText,
    changed_fields: changedFields,
    preview,
    editing_presence: editingPresence,
  };
}

function normalizeConflictListForUI(conflicts) {
  const rows = Array.isArray(conflicts) ? conflicts.map(normalizeConflictForUI) : [];
  return rows.filter((row, index, arr) => index === arr.findIndex((entry) => String(entry.conflict_id || '') === String(row.conflict_id || '') || `${entry.farm_id}|${entry.collection}|${entry.title}|${entry.entity_label}` === `${row.farm_id}|${row.collection}|${row.title}|${row.entity_label}`));
}

function formatCollectionLabelSafe(collection) {
  try { return collectionLabel(collection); } catch(e) {}
  return collection || 'Coleção';
}

function parseQueueEntityKey(entityKey = '') {
  const [farmId = '', collection = '', ...rest] = String(entityKey || '').split(':');
  return { farmId, collection, itemId: rest.join(':') || '' };
}

async function createPendingConflict(conflict, userName) {
  const meta = buildConflictSummary(conflict);
  const payload = {
    action: "create_conflict",
    farm_id: conflict?.farmId || conflict?.farm_id || "",
    farm_name: conflict?.farmName || conflict?.farm_name || "",
    collection: conflict?.collection || "",
    local_version: conflict?.local_version || 0,
    server_version: conflict?.server_version || 0,
    conflict_items: conflict?.conflict_items || [],
    local_data: conflict?.local_data || [],
    server_data: conflict?.server_data || [],
    merged_data: conflict?.merged_data || [],
    timestamp: conflict?.timestamp || new Date().toISOString(),
    title: meta.title,
    subject: meta.subject,
    entity_label: meta.entity_label,
    changed_fields: meta.changed_fields,
    record_count: meta.record_count,
    preview: meta.preview,
    summary_text: meta.summary_text,
  };
  try {
    const res = await apiCall(payload, userName);
    return res?.conflict || payload;
  } catch(e) {
    return { ...payload, conflict_id: conflict?.conflict_id || `local_${Date.now()}_${Math.random().toString(36).slice(2,8)}`, status: "pending_local_only" };
  }
}


// Version key helpers (stored locally alongside data)

export {
  PENDING_CONFLICTS_KEY,
  CONFLICT_RESOLUTIONS_KEY,
  SYNC_AUDIT_KEY,
  SYNC_HEALTH_KEY,
  USER_DELETION_LOG_KEY,
  buildDeletionTombstones,
  pruneDeletedAccounts,
  pruneDeletedEmployees,
  sanitizeGlobalCollectionForView,
  COLLECTION_LABELS,
  collectionLabel,
  describeConflictRecord,
  normalizeConflictFieldName,
  CONFLICT_FIELD_LABELS,
  CONFLICT_INTERNAL_FIELDS,
  CONFLICT_FIELD_ORDER,
  getFriendlyConflictFieldLabel,
  orderConflictKeys,
  formatConflictBusinessValue,
  getConflictSourceTimestamp,
  getConflictValueTone,
  buildConflictBusinessRows,
  INTERNAL_CONFLICT_FIELDS,
  isInternalConflictField,
  getMeaningfulConflictFields,
  formatConflictFieldLabel,
  formatSyncHistoryForUI,
  formatConflictValueForUI,
  getConflictRecordById,
  getConflictFieldValue,
  normalizeConflictFieldEntry,
  buildConflictSummary,
  normalizeConflictForUI,
  normalizeConflictListForUI,
  formatCollectionLabelSafe,
  parseQueueEntityKey,
  loadPendingConflictsFromStorage,
  savePendingConflictsToStorage,
  loadConflictResolutionsFromStorage,
  saveConflictResolutionsToStorage,
  loadSyncAuditFromStorage,
  saveSyncAuditToStorage,
  appendSyncAudit,
  saveSyncHealthSnapshot,
  createPendingConflict,
};
